import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog-no-overlay';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import { useEstimate, EstimateSettings } from '@/contexts/EstimateContext';
import { toast } from 'sonner';

interface EstimateSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EstimateSettingsModal: React.FC<EstimateSettingsModalProps> = ({
  open,
  onOpenChange,
}) => {
  const { settings, updateSettings } = useEstimate();

  // Local state for form values
  const [formValues, setFormValues] = useState<EstimateSettings>({
    taxRate: 0,
    surchargeRate: 0,
    markupRate: 0,
    shippingRate: 15,
    otherFee: 0,
    roundUpGlassSize: false,
  });

  // Update local state when settings change or modal opens
  useEffect(() => {
    if (open) {
      setFormValues(settings);
    }
  }, [open, settings]);

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = value === '' ? 0 : parseFloat(value);

    if (!isNaN(numValue)) {
      setFormValues(prev => ({
        ...prev,
        [name]: numValue,
      }));
    }
  };

  // Handle toggle changes
  const handleToggleChange = (name: string, checked: boolean) => {
    setFormValues(prev => ({
      ...prev,
      [name]: checked,
    }));
  };

  // Handle save
  const handleSave = () => {
    updateSettings(formValues);
    toast.success('Estimate settings updated');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1e293b] text-white border-[#4b5563] sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="text-[#60a5fa]">Estimate Settings</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="taxRate">Tax Rate (%)</Label>
            <Input
              id="taxRate"
              name="taxRate"
              type="number"
              min="0"
              step="0.01"
              value={formValues.taxRate}
              onChange={handleInputChange}
              className="bg-[#1e293b] text-white border-[#4b5563]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="surchargeRate">Surcharge Rate (%)</Label>
            <Input
              id="surchargeRate"
              name="surchargeRate"
              type="number"
              min="0"
              step="0.01"
              value={formValues.surchargeRate}
              onChange={handleInputChange}
              className="bg-[#1e293b] text-white border-[#4b5563]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="markupRate">Markup Rate (%)</Label>
            <Input
              id="markupRate"
              name="markupRate"
              type="number"
              min="0"
              step="0.01"
              value={formValues.markupRate}
              onChange={handleInputChange}
              className="bg-[#1e293b] text-white border-[#4b5563]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="shippingRate">Shipping Rate (%)</Label>
            <Input
              id="shippingRate"
              name="shippingRate"
              type="number"
              min="0"
              step="0.01"
              value={formValues.shippingRate}
              onChange={handleInputChange}
              className="bg-[#1e293b] text-white border-[#4b5563]"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="otherFee">Other Fee ($)</Label>
            <Input
              id="otherFee"
              name="otherFee"
              type="number"
              min="0"
              step="0.01"
              value={formValues.otherFee}
              onChange={handleInputChange}
              className="bg-[#1e293b] text-white border-[#4b5563]"
            />
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center space-x-2">
              <Label htmlFor="roundUpGlassSize" className="cursor-pointer">
                Round Up Glass Size
              </Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="bg-[#1e293b] text-white border-[#4b5563] max-w-xs">
                    <p>Rounds dimensions up to the next even inch for pricing purposes only. The displayed measurements remain unchanged.</p>
                    <p className="mt-2">This is typical practice from manufacturers providing quotes, as they often round up to the next even number for pricing calculations.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Switch
              id="roundUpGlassSize"
              checked={formValues.roundUpGlassSize}
              onCheckedChange={(checked) => handleToggleChange('roundUpGlassSize', checked)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            onClick={handleSave}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EstimateSettingsModal;
